// Copy for the Retail Profit Management page (src/pages/OurWorkRetailManagement.tsx, /our-work/retail-profit-management).
// The H1 "Retail Profit Management" is a product name and stays in English in the page.
// Analytics labels (trackEvent) stay in English in the page and are NOT taken from here.

type MetricId = 'ov' | 'net-sales' | 'gross-margin' | 'mktg-profit' | 'nc' | 'ebit';
type MetricDetail = { question: string; insight: string; action: string };
type WaterfallStepCopy = { label: string; fullName: string; deduction: string | null };
type InputFieldCopy = { label: string; placeholder: string; tooltip: string };
type InputKey = 'returnRate' | 'grossMargin' | 'mir' | 'paymentCost' | 'deliveryCost' | 'opex';

const en = {
  seoTitle: 'Retail Profit Management | Stratodata',
  seoDescription: 'Understand your true retail profitability: from Order Value to Net Contribution and EBIT. Visualise the profit waterfall and model your own numbers with our interactive calculator.',
  /** Formats a percentage value for display (calculation results, chart labels). */
  pct: (n: number): string => `${n}%`,
  hero: {
    tagline: 'Revenue is vanity. Margin is sanity. Profit is reality.',
    subtitle: 'We make the Walk to Profit visible — every day, every product, every channel.',
    ctaPrimary: 'Let\'s find your real profit',
    // Small line inside the primary button; empty = not shown
    ctaNote: '',
    ctaCalculator: 'Calculate your profitability',
  },
  provocations: [
    { q: 'What is your most important metric?', a: 'It is your net profit, isn\'t it?' as string | null },
    { q: 'And how often do you see it?', a: 'Most retailers see it quarterly. We make it daily.' as string | null },
    { q: 'What is the correct way?', a: null as string | null },
  ],
  correctWayLabel: 'The correct way',
  correctWay: [
    'See it precisely on accounting data.',
    'Evaluate EVERYTHING operational on your Net contribution margin.',
  ],
  waterfall: {
    title: 'The Walk to Profit',
    subtitle: 'Every step peels away a cost layer. Click any bar to understand the metric.',
    note: 'Illustrative percentages — your numbers will be different.',
    annotationOv: 'How most retailers evaluate daily performance',
    annotationNc: 'What we enable for them on daily basis',
    steps: {
      'ov': { label: 'Order\nValue', fullName: 'Order Value', deduction: null },
      'net-sales': { label: 'Net\nSales', fullName: 'Net Sales', deduction: 'Returns' },
      'gross-margin': { label: 'Gross\nMargin', fullName: 'Gross Margin', deduction: 'COGS' },
      'mktg-profit': { label: 'MKTG\nProfit', fullName: 'Marketing Profit', deduction: 'Cost' },
      'nc': { label: 'NC', fullName: 'Net Contribution', deduction: 'VC' },
      'ebit': { label: 'EBIT', fullName: 'EBIT', deduction: 'OPEX' },
    } as Record<MetricId, WaterfallStepCopy>,
  },
  detail: {
    remains: (value: number): string => `${value}% of Order Value remains`,
    question: 'The question',
    insight: 'The insight',
    action: 'The action',
    cta: 'Tell me more',
  },
  metricDetails: {
    'ov': {
      question: 'How much order value are we generating?',
      insight: 'Order value is the starting point — but your road to profit goes much deeper.',
      action: 'Track OV by channel, category, and time period to spot where the volume comes from. But do not stop there.',
    },
    'net-sales': {
      question: 'How much will we actually keep after returns from last day\'s order value?',
      insight: 'Returns silently eat 10–25% of order value. Return rates vary across product lines and markets significantly.',
      action: 'Make sure to account for it in your budgeting decisions before it is too late.',
    },
    'gross-margin': {
      question: 'What remains after cost of goods?',
      insight: 'COGS is the biggest single bite. Supplier negotiations and product mix are your biggest levers here.',
      action: 'Score every SKU by margin %. Make a strategy to grow GM% over time. Every percentage point counts.',
    },
    'mktg-profit': {
      question: 'Is our marketing actually profitable?',
      insight: 'Marketing spend and price are your two strongest controls of the ecommerce system.',
      action: 'Make sure to tune them continuously. There is no recipe other than ongoing testing.',
    },
    'nc': {
      question: 'What\'s our real contribution to the bottom line?',
      insight: 'Net Contribution strips out variable costs — the true measure of operational efficiency.',
      action: 'Evaluate EVERYTHING operational on your Net Contribution margin. This is your North Star for daily decisions.',
    },
    'ebit': {
      question: 'What\'s left as actual profit?',
      insight: 'OPEX EBIT is the finish line. Make sure to review on site level on monthly basis. Quarterly is too slow. Controlling needs to be set up.',
      action: 'Automate the walk-to-profit calculation. Surface it daily. Align every team to this single number.',
    },
  } as Record<MetricId, MetricDetail>,
  approach: {
    eyebrow: 'Our approach',
    title: 'From chaos to clarity in 3 phases',
    phases: [
      {
        phase: 'Phase 1',
        title: 'Connect & Model',
        duration: '2–4 weeks',
        description: 'We connect your ERP, e-commerce, and marketing data into a unified retail data model. Every SKU gets a margin, every channel gets a cost, every order gets a complete profit picture.',
      },
      {
        phase: 'Phase 2',
        title: 'Walk to Profit',
        duration: '2–3 weeks',
        description: 'We build your Walk to Profit chart, showing the exact path from Order Value through Returns, COGS, Marketing Cost and Variable Costs down to EBIT. On real accounting data, refreshed daily.',
      },
      {
        phase: 'Phase 3',
        title: 'Automate & Scale',
        duration: 'Ongoing',
        description: 'When the scoreboard is ready, the time for fun comes. Automate, optimize, agentify. But always come back to the scoreboard and evaluate, if it helped. If it does not manifest on the scoreboard, it was a miss.',
      },
    ],
    digitalCfo: {
      titleStart: 'Want this as a ',
      titleAccent: 'fully managed monthly controlling service?',
      textBefore: 'Across every channel and country, with ',
      textBold: 'plan, forecast and AI',
      textAfter: '.',
      button: 'Explore Digital CFO',
    },
  },
  calc: {
    eyebrow: 'DIY',
    title: 'Build Your Own Walk to Profit',
    subtitle: 'Enter your numbers. See where your money really goes.',
    allValues: 'All values in %',
    fields: {
      returnRate: { label: 'Return Rate', placeholder: 'e.g. 15', tooltip: '% of returns and claims from order value' },
      grossMargin: { label: 'Gross Margin', placeholder: 'e.g. 55', tooltip: '% of gross margin from net sales (order value - returns)' },
      mir: { label: 'MIR (Marketing Investment Ratio)', placeholder: 'e.g. 12', tooltip: '% of marketing cost on net sales (order value - returns)' },
      paymentCost: { label: 'Payment Cost', placeholder: 'e.g. 3', tooltip: '% of payment cost on net sales (order value - returns)' },
      deliveryCost: { label: 'Delivery Cost', placeholder: 'e.g. 5', tooltip: '% of delivery cost on net sales (order value - returns)' },
      opex: { label: 'OPEX', placeholder: 'e.g. 8', tooltip: '% of opex on net sales (order value - returns)' },
    } as Record<InputKey, InputFieldCopy>,
    calculate: 'Calculate',
    reset: 'Reset',
    resultTitle: 'Your Walk to Profit',
    ebitPositive: (value: string): string => `Your EBIT: ${value} of Net Sales`,
    ebitNegative: (value: string): string => `Your EBIT: ${value} of Net Sales — you are losing money`,
    /** Result chart steps, in the order computeWaterfall returns them (OV, Net Sales, GM, MKTG Profit, NC, EBIT). */
    steps: [
      { label: 'Order\nValue', fullLabel: 'Order Value', deduction: '' },
      { label: 'Net\nSales', fullLabel: 'Net Sales', deduction: 'Returns' },
      { label: 'Gross\nMargin', fullLabel: 'Gross Margin', deduction: 'COGS' },
      { label: 'MKTG\nProfit', fullLabel: 'Marketing Profit', deduction: 'MKTG' },
      { label: 'NC', fullLabel: 'Net Contribution', deduction: 'VC' },
      { label: 'EBIT', fullLabel: 'EBIT', deduction: 'OPEX' },
    ],
    ctaQuestion: 'Want to know how to move the numbers?',
    ctaButton: 'Free consultation on how to improve?',
  },
  finalCta: {
    title: 'See Your Real Profit',
    text: 'Stop guessing. Start managing every euro from order to EBIT.',
    button: 'Let\'s talk about your profit',
  },
};

const NBSP = ' ';

const sk: typeof en = {
  seoTitle: 'Retail Profit Management | Stratodata',
  seoDescription: 'Pochopte skutočnú ziskovosť svojho retailu: od hodnoty objednávok cez čistý príspevok na úhradu až po EBIT. Pozrite si vodopád zisku a namodelujte si vlastné čísla v interaktívnej kalkulačke.',
  // Slovak: decimal comma and a (non-breaking) space before %.
  pct: (n: number): string => `${String(n).replace('.', ',')}${NBSP}%`,
  hero: {
    tagline: 'Tržby sú pre parádu. Marža je zdravý rozum. Zisk je realita.',
    subtitle: 'Walk to Profit vám ukážeme každý deň, pre každý produkt a každý kanál.',
    ctaPrimary: 'Nájdime váš skutočný zisk',
    ctaNote: '',
    ctaCalculator: 'Vypočítajte si ziskovosť',
  },
  provocations: [
    { q: 'Aká je vaša najdôležitejšia metrika?', a: 'Je to čistý zisk, však?' },
    { q: 'A ako často ho vidíte?', a: 'Väčšina retailerov raz za štvrťrok. My vám ho ukážeme každý deň.' },
    { q: 'Ako to robiť správne?', a: null },
  ],
  correctWayLabel: 'Správny postup',
  correctWay: [
    'Vidieť ho presne, na účtovných dátach.',
    'Hodnotiť VŠETKO prevádzkové podľa čistého príspevku na úhradu.',
  ],
  waterfall: {
    title: 'Walk to Profit – cesta k zisku',
    subtitle: 'Každý krok uberie jednu vrstvu nákladov. Kliknite na stĺpec a uvidíte, čo metrika znamená.',
    note: 'Percentá sú ilustračné, vaše čísla budú iné.',
    annotationOv: 'Podľa tohto väčšina retailerov hodnotí denný výkon',
    annotationNc: 'Toto im umožníme sledovať každý deň',
    steps: {
      'ov': { label: 'Hodnota\nobjed\u00ADnávok', fullName: 'Hodnota objednávok', deduction: null },
      'net-sales': { label: 'Čisté\ntržby', fullName: 'Čisté tržby', deduction: 'Vratky' },
      'gross-margin': { label: 'Hrubá\nmarža', fullName: 'Hrubá marža', deduction: 'COGS' },
      'mktg-profit': { label: 'MKTG\nzisk', fullName: 'Marketingový zisk', deduction: 'Náklady' },
      'nc': { label: 'Čistý\npríspevok', fullName: 'Čistý príspevok na úhradu (Net Contribution)', deduction: 'VC' },
      'ebit': { label: 'EBIT', fullName: 'EBIT', deduction: 'OPEX' },
    },
  },
  detail: {
    remains: (value: number): string => `Z hodnoty objednávok zostáva ${value}${NBSP}%`,
    question: 'Otázka',
    insight: 'Zistenie',
    action: 'Čo s tým',
    cta: 'Chcem vedieť viac',
  },
  metricDetails: {
    'ov': {
      question: 'Akú hodnotu objednávok generujeme?',
      insight: 'Hodnota objednávok je len začiatok. Cesta k zisku vedie oveľa hlbšie.',
      action: 'Sledujte OV podľa kanálov, kategórií a obdobia, aby ste videli, odkiaľ objem prichádza. Tam však nekončite.',
    },
    'net-sales': {
      question: 'Koľko si zo včerajšej hodnoty objednávok po vratkách naozaj necháme?',
      insight: 'Vratky potichu zjedia 10 až 25 % hodnoty objednávok. Miera vratiek sa navyše výrazne líši podľa produktových radov a trhov.',
      action: 'Započítajte ich do rozpočtových rozhodnutí skôr, než bude neskoro.',
    },
    'gross-margin': {
      question: 'Čo zostane po odpočítaní nákladov na predaný tovar?',
      insight: 'COGS je najväčšie samostatné sústo. Najsilnejšími pákami sú tu vyjednávanie s dodávateľmi a produktový mix.',
      action: 'Ohodnoťte každé SKU podľa percenta marže. Pripravte si stratégiu, ako postupne zvyšovať hrubú maržu. Každý percentuálny bod sa počíta.',
    },
    'mktg-profit': {
      question: 'Je náš marketing naozaj ziskový?',
      insight: 'Marketingové výdavky a cena sú dve najsilnejšie páky, ktorými riadite svoj e-commerce.',
      action: 'Neustále ich dolaďujte. Iný recept než priebežné testovanie neexistuje.',
    },
    'nc': {
      question: 'Koľko reálne prispievame k výsledku hospodárenia?',
      insight: 'Čistý príspevok na úhradu odpočíta aj variabilné náklady. Preto skutočne ukazuje, ako efektívne fungujete.',
      action: 'VŠETKO prevádzkové hodnoťte podľa čistého príspevku na úhradu. To je vaša North Star metrika pre každodenné rozhodnutia.',
    },
    'ebit': {
      question: 'Čo nám zostane ako skutočný zisk?',
      insight: 'EBIT po odpočítaní OPEX je cieľová rovinka. Kontrolujte ho mesačne na úrovni e-shopu, štvrťročne je to príliš pomaly. Potrebujete mať nastavený controlling.',
      action: 'Automatizujte výpočet Walk to Profit a zobrazujte ho každý deň. Nech všetky tímy ťahajú za toto jedno číslo.',
    },
  },
  approach: {
    eyebrow: 'Náš prístup',
    title: 'Od chaosu k prehľadu v 3 fázach',
    phases: [
      {
        phase: 'Fáza 1',
        title: 'Prepojenie a dátový model',
        duration: '2 až 4 týždne',
        description: 'Dáta z vášho ERP, e-shopu a marketingu prepojíme do jednotného retailového dátového modelu. Každé SKU dostane maržu, každý kanál náklady a každá objednávka úplný obraz o zisku.',
      },
      {
        phase: 'Fáza 2',
        title: 'Walk to Profit',
        duration: '2 až 3 týždne',
        description: 'Postavíme vám Walk to Profit graf, ktorý presne ukáže cestu od hodnoty objednávok cez vratky, COGS, marketingové a variabilné náklady až po EBIT. Na reálnych účtovných dátach, aktualizovaný každý deň.',
      },
      {
        phase: 'Fáza 3',
        title: 'Automatizácia a škálovanie',
        duration: 'Priebežne',
        description: 'Keď je výsledková tabuľa pripravená, prichádza na rad zábava. Automatizujte, optimalizujte, nasaďte AI agentov. No vždy sa k výsledkovej tabuli vráťte a vyhodnoťte, či to pomohlo. Ak sa to na nej neprejaví, netrafili ste.',
      },
    ],
    digitalCfo: {
      titleStart: 'Máte záujem o ',
      titleAccent: 'plne spravovanú mesačnú controllingovú službu?',
      textBefore: 'Naprieč všetkými kanálmi a krajinami, s ',
      textBold: 'plánom, prognózou a AI',
      textAfter: '.',
      button: 'Spoznajte Digital CFO',
    },
  },
  calc: {
    eyebrow: 'Kalkulačka',
    title: 'Postavte si vlastný Walk to Profit',
    subtitle: 'Zadajte svoje čísla a uvidíte, kam vaše peniaze naozaj idú.',
    allValues: 'Všetky hodnoty v %',
    fields: {
      returnRate: { label: 'Miera vratiek', placeholder: 'napr. 15', tooltip: '% vratiek a reklamácií z hodnoty objednávok' },
      grossMargin: { label: 'Hrubá marža', placeholder: 'napr. 55', tooltip: '% hrubej marže z čistých tržieb (hodnota objednávok − vratky)' },
      mir: { label: 'MIR (podiel marketingových investícií)', placeholder: 'napr. 12', tooltip: '% marketingových nákladov z čistých tržieb (hodnota objednávok − vratky)' },
      paymentCost: { label: 'Náklady na platby', placeholder: 'napr. 3', tooltip: '% nákladov na platby z čistých tržieb (hodnota objednávok − vratky)' },
      deliveryCost: { label: 'Náklady na doručenie', placeholder: 'napr. 5', tooltip: '% nákladov na doručenie z čistých tržieb (hodnota objednávok − vratky)' },
      opex: { label: 'OPEX', placeholder: 'napr. 8', tooltip: '% prevádzkových nákladov (OPEX) z čistých tržieb (hodnota objednávok − vratky)' },
    },
    calculate: 'Vypočítať',
    reset: 'Vynulovať',
    resultTitle: 'Váš Walk to Profit',
    ebitPositive: (value: string): string => `Váš EBIT: ${value} z čistých tržieb`,
    ebitNegative: (value: string): string => `Váš EBIT: ${value} z čistých tržieb. Ste v strate.`,
    steps: [
      { label: 'Hodnota\nobjed\u00ADnávok', fullLabel: 'Hodnota objednávok', deduction: '' },
      { label: 'Čisté\ntržby', fullLabel: 'Čisté tržby', deduction: 'Vratky' },
      { label: 'Hrubá\nmarža', fullLabel: 'Hrubá marža', deduction: 'COGS' },
      { label: 'MKTG\nzisk', fullLabel: 'Marketingový zisk', deduction: 'MKTG' },
      { label: 'Čistý\npríspevok', fullLabel: 'Čistý príspevok na úhradu (Net Contribution)', deduction: 'VC' },
      { label: 'EBIT', fullLabel: 'EBIT', deduction: 'OPEX' },
    ],
    ctaQuestion: 'Chcete vedieť, ako tieto čísla posunúť?',
    ctaButton: 'Bezplatná konzultácia, ako sa zlepšiť?',
  },
  finalCta: {
    title: 'Pozrite sa na svoj skutočný zisk',
    text: 'Prestaňte hádať. Riaďte každé euro od objednávky až po EBIT.',
    button: 'Porozprávajme sa o vašom zisku',
  },
};

export const retailProfitManagementCopy = { en, sk };
